// A FEJLESZTÉS-form segédei: RHF-alak ↔ contract-body. A validálás forrás-igazsága a
// `@atlas/contracts` `ProductUpdateWriteBodySchema` — itt NINCS saját Zod-séma (a repo szabálya).
//
// A form LAPOS (nyelvenkénti mezők), a body viszont kétnyelvű OBJEKTUMOKAT vár: a lapítás/összeépítés itt
// történik, hogy a komponens ne alakítson adatot. Az ÜRES angol mező `undefined`-ként megy (nem üres string):
// így nem kerül üres fordítás a partner szemébe.
import { ProductUpdateWriteBodySchema, type ProductUpdateDetail } from '@atlas/contracts';
import { zodResolver } from '@hookform/resolvers/zod';
import type { Resolver } from 'react-hook-form';

export interface ProductUpdateFormValues {
  titleHu: string;
  titleEn: string;
  bodyHu: string;
  bodyEn: string;
  slug: string;
  stage: string;
  type: string;
  horizon: string;
  targetPeriod: string;
  shippedAt: string;
}

export const EMPTY_PRODUCT_UPDATE_FORM: ProductUpdateFormValues = {
  titleHu: '',
  titleEn: '',
  bodyHu: '',
  bodyEn: '',
  slug: '',
  stage: 'planned',
  type: 'feature',
  horizon: '',
  targetPeriod: '',
  shippedAt: '',
};

/** Egy meglévő tétel → form-alak (a kétnyelvű mezők lapítása). */
export function toProductUpdateForm(row: ProductUpdateDetail): ProductUpdateFormValues {
  return {
    titleHu: row.title.hu,
    titleEn: row.title.en ?? '',
    bodyHu: row.body?.hu ?? '',
    bodyEn: row.body?.en ?? '',
    slug: row.slug,
    stage: row.stage,
    type: row.type,
    horizon: row.horizon ?? '',
    targetPeriod: row.targetPeriod ?? '',
    shippedAt: row.shippedAt ?? '',
  };
}

const trimmedOrUndefined = (v: string): string | undefined => {
  const t = v.trim();
  return t === '' ? undefined : t;
};

/** Form-alak → contract-body. A hiányzó opcionális mező `undefined` (nem `null`, nem üres string). */
export function toProductUpdateBody(values: ProductUpdateFormValues): unknown {
  const en = trimmedOrUndefined(values.titleEn);
  const bodyHu = values.bodyHu.trim();
  return {
    slug: trimmedOrUndefined(values.slug),
    title: { hu: values.titleHu.trim(), ...(en === undefined ? {} : { en }) },
    // A rich-text mező üres állapota `<p></p>` — az számít üresnek (különben üres bekezdés mennne a partnerhez).
    body: isEmptyHtml(bodyHu)
      ? undefined
      : {
          hu: bodyHu,
          ...(isEmptyHtml(values.bodyEn.trim()) ? {} : { en: values.bodyEn.trim() }),
        },
    stage: values.stage,
    type: values.type,
    horizon: trimmedOrUndefined(values.horizon),
    targetPeriod: trimmedOrUndefined(values.targetPeriod),
    shippedAt: trimmedOrUndefined(values.shippedAt),
  };
}

/** A szerkesztő „üres" állapota: `<p></p>`, `<p><br></p>` vagy csupa whitespace. */
export function isEmptyHtml(html: string): boolean {
  return (
    html
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .trim() === ''
  );
}

/**
 * A resolver a CONTRACT sémájából: a form lapos mezőit a body-alakra fordítjuk, és a Zod hibáit
 * visszavezetjük a form-mezőkre (`title.hu` → `titleHu`), hogy a hiba a MEZŐ alatt jelenjen meg.
 */
export function buildProductUpdateResolver(): Resolver<ProductUpdateFormValues> {
  const base = zodResolver(ProductUpdateWriteBodySchema);
  return async (values, context, options) => {
    const body = toProductUpdateBody(values) as ProductUpdateFormValues;
    const result = await base(body, context, options as never);
    if (Object.keys(result.errors).length === 0) return { values, errors: {} };
    const mapped: Record<string, { type: string; message: string }> = {};
    for (const [path, err] of Object.entries(result.errors)) {
      const message = (err as { message?: string }).message ?? 'Érvénytelen érték.';
      const field = FIELD_BY_BODY_PATH[path] ?? path;
      mapped[field] = { type: 'validation', message };
    }
    return { values: {} as ProductUpdateFormValues, errors: mapped as never };
  };
}

/** A body-mező → form-mező leképezés (a hiba a helyes input alatt jelenjen meg). */
const FIELD_BY_BODY_PATH: Record<string, string> = {
  title: 'titleHu',
  'title.hu': 'titleHu',
  'title.en': 'titleEn',
  body: 'bodyHu',
  'body.hu': 'bodyHu',
  'body.en': 'bodyEn',
  slug: 'slug',
  stage: 'stage',
  type: 'type',
  horizon: 'horizon',
  targetPeriod: 'targetPeriod',
  shippedAt: 'shippedAt',
};
